// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import { barChart, lineChart, rankedBars, stackedArea } from './charts';
import { card, chartBox, legend, note, viewHead } from './ui';
import { gloss } from '../glossary';
import { esc, num, rate } from '../format';
import { COUNT_STOPS, FATAL, FATAL_LIGHT, countColor, divisionColor } from '../colors';
import { withDivisionDim } from '../data';
import type { Bundle, Val } from '../types';

export const title = 'Deaths';

/**
 * The source publishes full state names. Abbreviating them with a
 * first-letter-of-each-word rule turns the single-word states into "V", "Q"
 * and "T", which nobody reads as Victoria, Queensland and Tasmania.
 */
const STATE_ABBR: Record<string, string> = {
  'New South Wales': 'NSW',
  Victoria: 'Vic',
  Queensland: 'Qld',
  'South Australia': 'SA',
  'Western Australia': 'WA',
  Tasmania: 'Tas',
  'Northern Territory': 'NT',
  'Australian Capital Territory': 'ACT',
};

export function render(b: Bundle): string {
  const f = b.fatalities;
  const series = f.series;
  const years = series.map((d) => String(d.year));
  const counts: Val[] = series.map((d) => d.count);
  const rates: Val[] = series.map((d) => d.rate);
  const boundaryIdx = series.findIndex((d) => d.vintage === 'current');

  const maxCount = Math.max(...series.map((d) => d.count));
  const maxRate = Math.max(...series.map((d) => d.rate ?? 0));

  // Mechanism composition, 2003-2021, from the archive release — the only
  // long history that exists. It stops where the archive stops, and says so.
  const mh = f.mechanismHistory;
  const mhTotals = mh.names.map((_, k) => mh.years.reduce((a, y) => a + (y.values[k] ?? 0), 0));
  const topMech = mh.names
    .map((name, k) => ({ name, k, total: mhTotals[k] }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 7);
  const mechPalette = ['#41576b', '#7c6a55', '#5f7f6d', '#8a6d8f', '#a4795a', '#4f7d94', '#8c8577'];

  const mechSeries = topMech.map((m, i) => ({
    label: m.name,
    color: mechPalette[i % mechPalette.length],
    values: mh.years.map((y) => y.values[m.k] ?? 0),
  }));
  const otherValues = mh.years.map((y) =>
    y.values.reduce<number>((a, v, k) => a + (topMech.some((m) => m.k === k) ? 0 : v ?? 0), 0),
  );
  mechSeries.push({ label: 'All other mechanisms', color: '#b9b3aa', values: otherValues });

  // Current-vintage industry deaths, latest year.
  const industryRows = withDivisionDim(f.industry.rows);
  const lastIdx = f.industry.periods.length - 1;
  const topIndustry = industryRows
    .filter((r) => (r.v[lastIdx] ?? 0) > 0)
    .sort((a, b) => (b.v[lastIdx] ?? 0) - (a.v[lastIdx] ?? 0))
    .slice(0, 12);

  // Age x sex is a genuine cross-tab in this release, so it gets a matrix.
  const demo = f.demographics.rows;
  const ageGroups = demo.filter((r) => r.level === 1 && !r.total);
  const demoRows: { age: string; male: Val; female: Val }[] = [];
  {
    let currentAge = '';
    let male: Val = null;
    for (const r of demo) {
      if (r.total) continue;
      if (r.level === 1) { currentAge = r.name; male = null; continue; }
      if (r.name === 'Male') male = r.v[lastIdx];
      if (r.name === 'Female') demoRows.push({ age: currentAge, male, female: r.v[lastIdx] });
    }
  }
  const demoMax = Math.max(1, ...demoRows.flatMap((r) => [r.male ?? 0, r.female ?? 0]));

  return `
    ${viewHead(
      'Work deaths',
      'Who is being killed at work, and is it getting better?',
      `Twenty-two years of ${gloss('traumatic injury fatalities', 'traumatic injury fatality')} — people killed by an injury sustained doing their job. Deaths from occupational disease are not here: they take decades to appear, cannot be attributed to a year of exposure, and are undercounted in every country.`,
    )}

    ${card(
      'Deaths and the death rate, 2003 onwards',
      'The count is the number of people. The rate divides that by the size of the workforce, which is the only fair comparison across two decades of employment growth.',
      chartBox(
        lineChart({
          series: [
            { values: counts, color: FATAL, label: 'Deaths', width: 2.4 },
            { values: rates.map((r) => (r === null ? null : (r / maxRate) * maxCount)), color: FATAL_LIGHT, label: 'Rate', width: 2, dashed: true },
          ],
          labels: years,
          domain: [0, Math.ceil(maxCount / 50) * 50],
          width: 860,
          height: 300,
          fmtY: (v) => String(v),
          everyX: 2,
          markers: boundaryIdx > 0 ? [{ index: boundaryIdx, label: 'current release begins' }] : [],
          tip: (i) => `${years[i]}\n${num(counts[i])} workers killed\nRate ${rate(rates[i], 2)} per 100,000 workers\nSource: ${series[i].vintage === 'archive' ? '2021 archived release' : 'current release'}`,
        }),
        'wide',
      ) +
        legend([
          { color: FATAL, label: 'Deaths (left scale)' },
          { color: FATAL_LIGHT, label: 'Deaths per 100,000 workers (rescaled to share the axis)' },
        ]) +
        note(
          `<strong>Two releases, joined at ${f.boundary}.</strong> Safe Work Australia's current file only carries a rolling ten years, so everything before ${f.boundary} comes from the 2021 archived series. Counts are revised upward for years afterwards as coronial investigations conclude — ${f.revisions
            .map((r) => `${r.year} was ${r.archive}, now ${r.current}`)
            .join('; ')}. Every year the two releases share is taken from the current one.`,
          'warn',
        ),
    )}

    ${card(
      'Which ways of dying went away, and which did not',
      `Deaths by mechanism, 2003–${mh.years[mh.years.length - 1].year}. Only the archived release breaks the long history down this way, so this chart stops earlier than the one above.`,
      chartBox(
        stackedArea({
          series: mechSeries,
          labels: mh.years.map((y) => String(y.year)),
          width: 860,
          height: 320,
          fmtY: (v) => String(Math.round(v)),
          tip: (i) => {
            const y = mh.years[i];
            const lines = mechSeries
              .map((s) => `${s.label}: ${s.values[i]}`)
              .join('\n');
            return `${y.year}\n${lines}`;
          },
        }),
        'wide',
      ) + legend(mechSeries.map((s) => ({ color: s.color, label: s.label }))),
    )}

    ${card(
      `Deaths by industry, ${f.industry.periods[lastIdx]}`,
      'ANZSIC subdivisions, current release. Coloured by division so the same industry keeps the same colour everywhere on this site.',
      rankedBars(
        topIndustry.map((r) => ({
          label: r.name,
          value: r.v[lastIdx],
          display: num(r.v[lastIdx]),
          color: divisionColor(r.division),
          sub: r.divisionName,
          tip: `${r.name}\n${r.divisionName}\n${num(r.v[lastIdx])} deaths in ${f.industry.periods[lastIdx]}\n${num(r.v.reduce<number>((a, v) => a + (v ?? 0), 0))} over the ten years shown`,
        })),
      ),
    )}

    ${card(
      `Who dies, by age and sex, ${f.industry.periods[lastIdx]}`,
      'One of the few genuine cross-tabulations in this release: age and sex are published together, so this is a real joint distribution rather than two separate lists laid side by side.',
      `<div class="mx-wrap"><table class="mx">
        <thead><tr><th class="mx-corner">Age group</th><th scope="col">Male</th><th scope="col">Female</th><th scope="col">Total</th></tr></thead>
        <tbody>${demoRows
          .map((r) => {
            const t = (r.male ?? 0) + (r.female ?? 0);
            const cell = (v: Val, label: string) =>
              `<td class="mx-cell" style="background:${countColor((v ?? 0) / demoMax)}" data-tip="${esc(`${label}, ${r.age}: ${num(v)} deaths in ${f.industry.periods[lastIdx]}`)}"><span>${num(v)}</span></td>`;
            return `<tr><th scope="row">${esc(r.age)}</th>${cell(r.male, 'Male')}${cell(r.female, 'Female')}<td class="mx-total">${num(t)}</td></tr>`;
          })
          .join('')}</tbody></table></div>` +
        `<div class="legend"><span class="legend-item">Fewer<span class="ramp">${COUNT_STOPS.map((c) => `<i style="background:${c}"></i>`).join('')}</span>More deaths</span></div>` +
        note(
          `Of the ${ageGroups.length} age groups, the overwhelming majority of these deaths are men. That is not a reporting artefact — it reflects who works in the industries where fatal injuries happen.`,
        ),
    )}

    ${card(
      `Deaths by state, ${f.state.periods[lastIdx]}`,
      'Where the death occurred, not where the employer is registered. Small states have small counts, so a single multi-fatality incident moves a whole year.',
      chartBox(
        barChart({
          values: f.state.rows.filter((r) => !r.total).map((r) => r.v[lastIdx]),
          labels: f.state.rows.filter((r) => !r.total).map((r) => STATE_ABBR[r.name] ?? r.name),
          domain: [0, Math.max(...f.state.rows.filter((r) => !r.total).map((r) => r.v[lastIdx] ?? 0)) * 1.15],
          width: 760,
          height: 240,
          color: () => FATAL,
          fmtY: (v) => String(Math.round(v)),
          tip: (i) => {
            const row = f.state.rows.filter((r) => !r.total)[i];
            return `${row.name}\n${num(row.v[lastIdx])} deaths in ${f.state.periods[lastIdx]}`;
          },
        }),
        'wide',
      ),
    )}
  `;
}
